import { z } from "zod";

import {
  RequiredTestGroupSchema,
  TestGroupDtoSchema,
  TestGroupSchema,
} from "../dtos/deprecated/test-groups";
import { TrafficConfigDtoSchema } from "../dtos/deprecated/traffic-configs";
import { EstimatedTestLengthRangeSchema } from "../dtos/experience-analysis";
import { UnknownExperienceIntegrationDto } from "../dtos/experience-integrations";
import { ExperienceProductDtoSchema } from "../dtos/experience-products";
import {
  ExperienceCategory,
  ExperienceCurrencyDto,
  ExperienceCurrencyDtoSchema,
  ExperienceDevicePreviewDtoSchema,
  ExperienceDto,
  ExperienceDtoSchema,
  ExperienceStatusList,
  ExperienceTestTypeDto,
  ExperienceType,
  ExperienceTypeList,
} from "../dtos/experiences";
import { UserInterfaceDtoSchema } from "../dtos/user-interfaces";

import { AnalyticsViewTypeList } from "./analytics/base";
import { DatasetSchema } from "./analytics/metrics";
import { OfferDiscountLikeObject } from "./offers";
import {
  ExperienceExclusionType,
  PluginAudienceType,
  PluginEntityType,
} from "./plugin";
import { VariationLikeObject } from "./variations";
import { WidgetTypeType } from "./widgets";

export const ExperienceTestTypeList = [
  "pricing",
  "shipping",
  "campaign",
  "content",
] as const;
export type ExperienceTestType = (typeof ExperienceTestTypeList)[number];

export const ExperienceAnalyticStatusList = [
  "Gathering Data",
  "Ready for Analysis",
  "Complete",
] as const;
export type ExperienceAnalyticsStatus =
  (typeof ExperienceAnalyticStatusList)[number];

const GoogleAnalyticsSchema = z.object({
  customDimensionIndex: z.number().nullish(),
});
export type GoogleAnalyticsType = z.infer<typeof GoogleAnalyticsSchema>;

export const RequiredExperienceSchema = z.object({
  id: z.string().uuid().optional(),
  name: z.string(),
  hasTestPricing: z.boolean().default(true),
  hasTestShipping: z.boolean().default(false),
  hasTestCampaign: z.boolean().default(false),
  hasTestContent: z.boolean().default(false),
  isPreview: z.boolean().optional().default(true),
  description: z.string().nullish(),
  createdAtTs: z.number().optional(),
  startedAtTs: z.number().nullish(),
  pausedAtTs: z.number().nullish(),
  endedAtTs: z.number().nullish(),
  status: z.enum(ExperienceStatusList).nullish(),
  progress: z.number().nullish(),
  analysisUrl: z.string().nullish(),
  controlMonthly: z.number().nullish(),
  excludeFromImpact: z.boolean().default(false).optional(),
  testGroups: RequiredTestGroupSchema.array(),
  impact: z.number().nullish(),
  analyticsViewType: z.enum(AnalyticsViewTypeList).default("none").optional(),
  estimatedTestLengthRange: EstimatedTestLengthRangeSchema.default({
    low: null,
    high: null,
  }).optional(),
  impactMetric: z.string().nullish(),
  devicePreview: ExperienceDevicePreviewDtoSchema.nullish(),
  previewPath: z.string().nullish(),
  visitors: z.number().optional(),
  orders: z.number().optional(),
  testFlowType: z.enum(ExperienceTypeList),
  goal: z.string().nullish(),
  isExternal: z.boolean().default(false).nullish(),
  currency: ExperienceCurrencyDtoSchema.optional().nullish(),
});
export type RequiredExperienceType = z.infer<typeof RequiredExperienceSchema>;

export const EnsuredExperienceStateSchema = ExperienceDtoSchema.extend({
  testGroups: TestGroupDtoSchema.array(),
  trafficConfig: TrafficConfigDtoSchema,
  testProducts: ExperienceProductDtoSchema.array().default([]),
  devicePreview: ExperienceDevicePreviewDtoSchema,
  allTestProductsLoaded: z.boolean().default(false),
});

type _EnsuredExperienceStateType = z.infer<typeof EnsuredExperienceStateSchema>;

export const ExperienceStateWithTrafficConfigs = ExperienceDtoSchema.extend({
  trafficConfig: TrafficConfigDtoSchema,
});
export type ExperienceStateWithTrafficConfigType = z.infer<
  typeof ExperienceStateWithTrafficConfigs
>;

export const ExperienceWithIdSchema = ExperienceDtoSchema.extend({
  id: z.string().uuid(),
  testGroups: TestGroupSchema.array(),
  trafficConfig: TrafficConfigDtoSchema.nullish(),
  datasets: DatasetSchema.nullish(),
  userInterfaces: UserInterfaceDtoSchema.array().optional().default([]),
});
type _ExperienceWithIdType = z.infer<typeof ExperienceWithIdSchema>;

export type ExperienceWithIdType = ExperienceDto & _ExperienceWithIdType;

export const ProgressBarConfigSchema = z.object({
  subtotalQuerySelector: z.string(),
  defaultThreshold: z.string(),
  minimumPurchaseAmount: z.string(),
  underMinimumText: z.string(),
  underThresholdText: z.string(),
  overThresholdText: z.string(),
  maxWidth: z.string(),
  textColor: z.string(),
  textSize: z.string(),
  underMinimumBackgroundColor: z.string(),
  progressBarBackgroundColor: z.string(),
  progressBarCompletedColor: z.string(),
  progressBarHeight: z.string(),
  showWhileCartIsEmpty: z.boolean().optional(),
  enabled: z.boolean().optional(),
});
export type ProgressBarConfigType = z.infer<typeof ProgressBarConfigSchema>;

export interface ExperienceLikeObject {
  id: string;
  name: string;
  entityType: PluginEntityType;
  type: ExperienceType;
  category: ExperienceCategory;
  description?: string | null;
  isPreview: boolean;
  pausedAtTs: number | null;
  hasBannerConfig: boolean;
  hasTestPricing: boolean;
  hasTestCampaign: boolean;
  hasTestShipping: boolean;
  isDiscountEnabled: boolean;
  forceExcludeEntireExperience: boolean;
  requiresLink: boolean;
  currency?: ExperienceCurrencyDto;
  audience?: PluginAudienceType;
  variations: VariationLikeObject[] | null;
  controlVariation: VariationLikeObject | null;
  measurementId: string | null;
  experienceIntegrations: UnknownExperienceIntegrationDto[] | null;
  testTypes: ExperienceTestTypeDto;
  getVariation: () => VariationLikeObject | null;

  getProductIdFromVariantId(variantId: string): string | null;

  determinePageTargetingIncluded: (id: string) => boolean;
  determineGlobalAudienceExclusions: (options?: any) => {
    trafficExclusion: false | ExperienceExclusionType;
    newExclusions?: string[];
  };
  getWidget: <T>(widgetType: WidgetTypeType) => T | undefined;
  hasWidget: (widgetType: WidgetTypeType) => boolean;
  getProgressBarBuildRequirements: () => {
    discount: OfferDiscountLikeObject;
  } | null;
}

export interface SitewideLikeObject {
  id: string;
  entityType: PluginEntityType;
  getWidget: <T>(widgetType: WidgetTypeType) => T | undefined;
  hasWidget: (widgetType: WidgetTypeType) => boolean;
  getProgressBarBuildRequirements: () => {
    discount: OfferDiscountLikeObject;
  } | null;
}
